/**
 * CONTRACT.md §2.14 — the disclosure and the empty state.
 *
 * `Fold` is a native `<details>`, which is the whole point: it works with
 * JavaScript broken, it is one element rather than a widget, and the `+` / `−`
 * marker is `::before` in portal.css. §2.14: "Do not add your own arrow."
 *
 * The summary must stay a list-item (see portal.css): restyling it as flex
 * drops it out of the accessibility tree in Chromium and makes the painted
 * label miss clicks. Do not "fix" keyboard support by wrapping a button —
 * that is a second implementation of what `<summary>` already is.
 *
 * Uncontrolled by default (`defaultOpen`), controlled when a page passes
 * `open` **and** `onToggle`. `<details>` changes its own state on click, so a
 * controlled one has to hear the browser's `toggle` event and answer with the
 * value it wants — which is what `onToggle` is for.
 *
 * `Empty` is ONE muted line. §3: "no wrapper panel, no `<b>`, no paragraph",
 * and `.empty__mark` was deleted. Inside a `CardBody tight` it picks up the
 * card's edge padding from app.css alone.
 */

import { useEffect, useRef, type ReactNode } from 'react'

import type { Common } from './common.js'

export type FoldProps = Common & {
  /** The summary line. Plain sans; the marker is added by app.css. */
  readonly summary: ReactNode
  /** Uncontrolled: how it starts. */
  readonly defaultOpen?: boolean | undefined
  /** Controlled: what it is. Pass `onToggle` with it. */
  readonly open?: boolean | undefined
  readonly onToggle?: ((open: boolean) => void) | undefined
  readonly children: ReactNode
}

export function Fold({ summary, defaultOpen, open, onToggle, children, ...common }: FoldProps) {
  const element = useRef<HTMLDetailsElement>(null)

  /*
   * A controlled `<details>` still toggles itself on click — `open` is a DOM
   * property the browser writes, not one React owns, and React never hears about
   * it. Two things put it back, and both are needed:
   *
   *   - the handler, for the click itself. The browser has already opened it by
   *     the time `toggle` fires; the page is told, and if the page does not
   *     change its mind there is no re-render to correct the element, so it is
   *     corrected here.
   *   - the effect, for a page that opens it from somewhere else. React updates
   *     the attribute when the prop changes, but only when its own copy of the
   *     old value is right — and after a click it is not.
   */
  useEffect(() => {
    const node = element.current
    if (node === null || open === undefined) return
    if (node.open !== open) node.open = open
  })

  return (
    <details
      className="fold"
      ref={element}
      open={open ?? defaultOpen}
      onToggle={
        onToggle === undefined
          ? undefined
          : (event) => {
              const next = event.currentTarget.open
              onToggle(next)
              if (open !== undefined && next !== open) event.currentTarget.open = open
            }
      }
      {...common}
    >
      <summary>{summary}</summary>
      {children}
    </details>
  )
}

export type EmptyProps = Common & { readonly children: ReactNode }

/** One muted line. No icon, no panel, no paragraph of consolation. */
export function Empty({ children, ...common }: EmptyProps) {
  return (
    <p className="empty" {...common}>
      {children}
    </p>
  )
}
